// src/utils/badgeColor.js
//
// Vytáhne dominantní (nejvýraznější) barvu z badge/vlajky týmu. Používá se
// k obarvení pozadí MatchCard jemným gradientem z barvy domácích (vlevo) do
// barvy hostů (vpravo). Barvy se počítají na malém canvasu z PNG badge a
// cachují podle URL, takže se každý obrázek zpracuje jen jednou.

const backendUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

// Cache hotových barev (Promise) podle URL obrázku.
const colorCache = new Map();

export const getBadgeUrl = (teamId) =>
	backendUrl ? `${backendUrl}/badges/${teamId}.png` : null;

// Deterministický fallback, když se barvu z obrázku nepodaří přečíst
// (CORS / tainted canvas / chybějící obrázek). Z ID/jména týmu odvodíme
// stabilní příjemný odstín, takže karta nikdy nezůstane úplně bez barvy.
const fallbackColor = (seed) => {
	const str = String(seed ?? '');
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
		hash |= 0;
	}
	const hue = Math.abs(hash) % 360;
	return hslToRgb(hue, 55, 55);
};

const hslToRgb = (h, s, l) => {
	s /= 100;
	l /= 100;
	const k = (n) => (n + h / 30) % 12;
	const a = s * Math.min(l, 1 - l);
	const f = (n) =>
		Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))));
	return [f(0), f(8), f(4)];
};

const rgbToHsl = (r, g, b) => {
	r /= 255; g /= 255; b /= 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r: h = (g - b) / d + (g < b ? 6 : 0); break;
			case g: h = (b - r) / d + 2; break;
			default: h = (r - g) / d + 4;
		}
		h *= 60;
	}
	return [h, s * 100, l * 100];
};

// Posun světlosti barvy o `delta` (-1..1). Zachová odstín i sytost. Slouží jako
// poslední záchrana, když mají oba týmy jen jednu (a tu samou) dominantní barvu –
// rozprostřením tónů (jeden světlejší, druhý tmavší) gradient nezůstane plochý.
const adjustLightness = ([r, g, b], delta) => {
	const [h, s, l] = rgbToHsl(r, g, b);
	const nl = Math.min(100, Math.max(0, l + delta * 100));
	return hslToRgb(h, s, nl);
};

// Perceptuálně laděná vzdálenost dvou RGB barev („redmean"). 0 = shodné;
// nepodobné barvy (červená vs. modrá) jdou do stovek. Používá se k rozpoznání,
// zda mají oba týmy příliš blízké dominantní barvy.
export const colorDistance = (a, b) => {
	const rmean = (a[0] + b[0]) / 2;
	const dr = a[0] - b[0];
	const dg = a[1] - b[1];
	const db = a[2] - b[2];
	return Math.sqrt((2 + rmean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rmean) / 256) * db * db);
};

// [r,g,b] → CSS string.
export const rgbToCss = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;

// "#RRGGBB" / "#RGB" → [r,g,b]; null pro prázdnou/nevalidní hodnotu.
export const hexToRgb = (hex) => {
	if (typeof hex !== 'string') return null;
	const m = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
	if (!m) return null;
	let h = m[1];
	if (h.length === 3) h = h.split('').map((c) => c + c).join('');
	return [
		parseInt(h.slice(0, 2), 16),
		parseInt(h.slice(2, 4), 16),
		parseInt(h.slice(4, 6), 16),
	];
};

// Převede barvu na odstín šedi (luminance, stejně jako CSS filter grayscale).
// Používá se k „zešednutí" pozadí a stínu poraženého týmu na MatchCard, aby
// ladily s jeho zešedlým logem.
export const toGrayscale = ([r, g, b]) => {
	const y = Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
	return [y, y, y];
};

// Relativní luminance (WCAG) – pro odhad, zda je barva dost tmavá na text/akcent
// proti bílému pozadí. 0 = černá, 1 = bílá.
const relLuminance = ([r, g, b]) => {
	const lin = (c) => {
		const x = c / 255;
		return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
	};
	return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

// Upraví barvu tak, aby fungovala jako text/akcent na světlém podkladu. Posílí
// sytost a postupně snižuje světlost, dokud barva není dost tmavá (kontrast proti
// bílé ~4:1). Inherentně světlé odstíny (žlutá, azurová) tak ztmavnou na čitelný
// tón, tmavé barvy (navy) zůstanou. Zachová odstín.
export const readableOnLight = ([r, g, b]) => {
	const [h, s0, l0] = rgbToHsl(r, g, b);
	const s = Math.max(s0, 45);
	let l = Math.min(l0, 50);
	let out = hslToRgb(h, s, l);
	// Cílíme luminanci ≤ 0.2 (kontrast proti bílé ≈ 4.2:1).
	for (let i = 0; i < 50 && relLuminance(out) > 0.2 && l > 0; i++) {
		l = Math.max(0, l - 3);
		out = hslToRgb(h, s, l);
	}
	return out;
};

// Z pixelů malého canvasu vybere malou paletu dominantních barev, seřazenou
// od nejvýraznější. Ignoruje průhledné, téměř bílé a téměř černé pixely
// (rámečky, pozadí), aby barvy odpovídaly skutečné dominantě, ne bílému okraji.
// Vrací pole [[r,g,b], …] vzájemně dostatečně odlišných barev (max 4). První
// prvek = hlavní dominantní barva; další slouží jako náhrada, když mají oba
// týmy příliš blízké hlavní barvy.
const pickPalette = (data) => {
	const buckets = new Map();
	let fallbackR = 0;
	let fallbackG = 0;
	let fallbackB = 0;
	let fallbackCount = 0;

	for (let i = 0; i < data.length; i += 4) {
		const r = data[i];
		const g = data[i + 1];
		const b = data[i + 2];
		const alpha = data[i + 3];

		if (alpha < 125) continue;

		fallbackR += r;
		fallbackG += g;
		fallbackB += b;
		fallbackCount++;

		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		// Přeskoč skoro bílé, skoro černé a šedé (nízká sytost) pixely.
		if (max > 235 && min > 215) continue;
		if (max < 45) continue;
		if (max - min < 22) continue;

		// Kvantizace do hrubších kbelíků, aby se podobné odstíny sčítaly.
		const key =
			((r >> 5) << 10) | ((g >> 5) << 5) | (b >> 5);
		const bucket = buckets.get(key);
		if (bucket) {
			bucket.r += r;
			bucket.g += g;
			bucket.b += b;
			bucket.count++;
		} else {
			buckets.set(key, { r, g, b, count: 1 });
		}
	}

	// Kbelíky seřadíme podle četnosti a hladově vybereme barvy, které jsou
	// dostatečně odlišné od už vybraných (aby paleta nebyla pět odstínů téhož).
	const sorted = [...buckets.values()]
		.sort((x, y) => y.count - x.count)
		.map((x) => [
			Math.round(x.r / x.count),
			Math.round(x.g / x.count),
			Math.round(x.b / x.count),
		]);

	const palette = [];
	for (const c of sorted) {
		if (palette.every((p) => colorDistance(p, c) > 45)) palette.push(c);
		if (palette.length >= 4) break;
	}

	if (palette.length) return palette;

	if (fallbackCount > 0) {
		return [[
			Math.round(fallbackR / fallbackCount),
			Math.round(fallbackG / fallbackCount),
			Math.round(fallbackB / fallbackCount),
		]];
	}

	return null;
};

// Spočítá, kolikrát je potřeba obrázek přizoomovat, aby neprůhledný obsah
// (vlajka) vyplnil celý čtverec/kruh. Národní vlajky jsou v PNG vykreslené
// s průhledným pruhem nahoře/dole (letterbox), různě velkým podle poměru
// stran vlajky – fixní zoom proto u některých nestačí. Z alfa kanálu najdeme
// těsný obdélník obsahu a vrátíme `max(šířka, výška) / obsah`. Výsledek
// ořízneme na rozumný rozsah, aby ojedinělý šum/stín nezpůsobil extrém.
const computeFillScale = (data, size) => {
	let top = size, bottom = -1, left = size, right = -1;

	for (let y = 0; y < size; y++) {
		for (let x = 0; x < size; x++) {
			const alpha = data[(y * size + x) * 4 + 3];
			if (alpha < 130) continue;
			if (y < top) top = y;
			if (y > bottom) bottom = y;
			if (x < left) left = x;
			if (x > right) right = x;
		}
	}

	if (bottom < 0 || right < 0) return 1; // nic neprůhledného → nezoomovat

	const contentH = bottom - top + 1;
	const contentW = right - left + 1;
	// Bbox je kvantizovaný na celé pixely canvasu (obsah se změří o kousek
	// větší, než skutečně je) → zoom by vyšel těsně malý a v kruhu by nahoře
	// a dole zůstala vlásková mezera. Malý přesah (2 %) chybu spolehlivě
	// překryje; ořízne jen okraj vlajky, což není vidět.
	const scale = Math.max(size / contentW, size / contentH) * 1.02;

	// Clamp: nikdy nezmenšovat (<1) a nepřezoomovat přes 2.6×.
	return Math.min(Math.max(scale, 1), 2.6);
};

const fallbackResult = (seed) => {
	const color = fallbackColor(seed);
	return { color, palette: [color], fillScale: 1.5 };
};

/**
 * Vrátí Promise s daty badge týmu: `{ color: [r,g,b], palette, fillScale }`.
 *  - `color`     – dominantní barva (pro barevný stín pod kruhem),
 *  - `palette`   – malá paleta dominantních barev (první = `color`), používá se
 *                  k odlišení pozadí, když mají oba týmy blízké hlavní barvy,
 *  - `fillScale` – kolik zoomu vlajka potřebuje, aby vyplnila kruh.
 * Výsledky cachuje podle URL. Při jakékoli chybě (CORS, chybějící obrázek)
 * vrací deterministický fallback odvozený ze `seed` (id/jméno týmu).
 *
 * @param {string|number} teamId
 * @param {string|number} seed - id nebo jméno týmu pro fallback
 * @returns {Promise<{color: [number, number, number], palette: number[][], fillScale: number}>}
 */
export const getDominantColor = (teamId, seed) => {
	const url = getBadgeUrl(teamId);
	if (!url) return Promise.resolve(fallbackResult(seed ?? teamId));

	if (colorCache.has(url)) return colorCache.get(url);

	const promise = new Promise((resolve) => {
		const img = new Image();
		img.crossOrigin = 'anonymous';

		img.onload = () => {
			try {
				// Dost velké rozlišení, aby změřený letterbox vlajky (computeFillScale)
				// neměl velkou kvantizační chybu – na 32px canvasu vycházel zoom až
				// o ~6 % menší a vlajka nevyplnila kruh. Pro paletu je 128px také OK.
				const size = 128;
				const canvas = document.createElement('canvas');
				canvas.width = size;
				canvas.height = size;
				const ctx = canvas.getContext('2d', { willReadFrequently: true });
				ctx.drawImage(img, 0, 0, size, size);
				const { data } = ctx.getImageData(0, 0, size, size);
				const palette = pickPalette(data) || [fallbackColor(seed ?? teamId)];
				resolve({ color: palette[0], palette, fillScale: computeFillScale(data, size) });
			} catch (e) {
				// Tainted canvas (chybějící CORS hlavičky) apod.
				resolve(fallbackResult(seed ?? teamId));
			}
		};

		img.onerror = () => resolve(fallbackResult(seed ?? teamId));
		img.src = url;
	});

	colorCache.set(url, promise);
	return promise;
};

/**
 * Barvy týmu pro MatchCard: výsledek `getDominantColor`, přes který se položí
 * ruční barvy z backendu (`team.primaryColor` / `team.secondaryColor`, hex),
 * pokud jsou vyplněné. Primární barva nahradí dominantní, sekundární první
 * náhradu v paletě; zbytek detekované palety zůstává jako další zálohy pro
 * `deconflictColors`. `fillScale` (zoom vlajek) se vždy bere z obrázku.
 *
 * @param {{id: number|string, name?: string, primaryColor?: string, secondaryColor?: string}} team
 * @returns {Promise<{color: [number, number, number], palette: number[][], fillScale: number}>}
 */
export const getTeamColors = (team) =>
	getDominantColor(team.id, team.name).then((res) => {
		const primary = hexToRgb(team.primaryColor);
		if (!primary) return res;
		const secondary = hexToRgb(team.secondaryColor);
		const rest = res.palette.filter(
			(c) => colorDistance(c, primary) > 45 && (!secondary || colorDistance(c, secondary) > 45),
		);
		const palette = secondary ? [primary, secondary, ...rest] : [primary, ...rest];
		return { ...res, color: primary, palette };
	});

// Pod touto vzdáleností (redmean) považujeme dvě barvy za „příliš blízké".
const TOO_CLOSE_DISTANCE = 95;

// Rozdíl dvou odstínů na barevném kruhu (0–180°).
const hueDistance = (h1, h2) => {
	const d = Math.abs(h1 - h2) % 360;
	return d > 180 ? 360 - d : d;
};

// Práh „stejného odstínu": dvě syté barvy s odstínem blíž než tolik stupňů
// (např. dvě červené různého jasu) čteme jako jednu barvu, i když je jejich
// redmean vzdálenost kvůli rozdílnému jasu velká.
const SAME_HUE_DEGREES = 24;
// Pod touto sytostí (%) je odstín nespolehlivý (skoro šedá/černá/bílá), takže
// hue-test neaplikujeme a spoléháme jen na redmean vzdálenost.
const MIN_SATURATION = 25;

// True, když jsou dvě barvy vizuálně příliš blízké: buď je malá perceptuální
// (redmean) vzdálenost, NEBO mají obě dost syté barvy skoro stejný odstín.
// Druhá podmínka chytá případy jako „obě červené, jen jiný jas", které samotná
// redmean vzdálenost propustí jako odlišné.
const tooClose = (a, b) => {
	if (colorDistance(a, b) < TOO_CLOSE_DISTANCE) return true;
	const [ha, sa] = rgbToHsl(a[0], a[1], a[2]);
	const [hb, sb] = rgbToHsl(b[0], b[1], b[2]);
	return sa >= MIN_SATURATION && sb >= MIN_SATURATION && hueDistance(ha, hb) <= SAME_HUE_DEGREES;
};

// Z kandidátů vybere barvu nejvzdálenější od reference.
const farthestFrom = (candidates, ref) => {
	let best = null;
	let bestDist = -1;
	for (const c of candidates) {
		const d = colorDistance(ref, c);
		if (d > bestDist) { bestDist = d; best = c; }
	}
	return best ? { color: best, dist: bestDist } : null;
};

/**
 * Z palet obou týmů zvolí dvojici barev pro pozadí MatchCard tak, aby se vždy
 * vizuálně lišily. Když jsou hlavní barvy dostatečně odlišné, vrátí je beze
 * změny. Když jsou příliš blízko, sáhne po sekundární barvě toho týmu, který má
 * nejodlišnější náhradu. A když ani jeden tým druhou barvu nemá (mono-barevné
 * logo), rozprostře tóny – domácí o něco světlejší, hosté tmavší.
 *
 * @param {number[][]} homePalette - paleta domácích (z getDominantColor().palette)
 * @param {number[][]} awayPalette - paleta hostů
 * @returns {{home: number[], away: number[], adjusted: boolean}}
 */
export const deconflictColors = (homePalette, awayPalette) => {
	const home0 = (homePalette && homePalette[0]) || [120, 120, 120];
	const away0 = (awayPalette && awayPalette[0]) || [120, 120, 120];

	if (!tooClose(home0, away0)) {
		return { home: home0, away: away0, adjusted: false };
	}

	// Jako náhradu bereme jen ty sekundární barvy, které samy nejsou příliš
	// blízké hlavní barvě druhého týmu (jinak bychom jednu červenou jen vyměnili
	// za jinou červenou). Z nich pak vybereme tu nejvzdálenější.
	const altAway = farthestFrom(
		(awayPalette || []).slice(1).filter((c) => !tooClose(c, home0)),
		home0,
	);
	const altHome = farthestFrom(
		(homePalette || []).slice(1).filter((c) => !tooClose(c, away0)),
		away0,
	);

	// Vybereme tu náhradu (z domácích nebo hostů), která dá větší odstup.
	if (altAway && (!altHome || altAway.dist >= altHome.dist)) {
		return { home: home0, away: altAway.color, adjusted: true };
	}
	if (altHome) {
		return { home: altHome.color, away: away0, adjusted: true };
	}

	// Žádná dost odlišná sekundární barva → rozprostřeme tóny stejné barvy.
	return {
		home: adjustLightness(home0, 0.16),
		away: adjustLightness(away0, -0.18),
		adjusted: true,
	};
};
